import fecha from 'fecha';
import keyboard from './utils/keyboard';
import {parseJson} from "./utils/json-parse";
import {processAxiosConfig} from "./utils/process.axios";

const SPECIAL_CHARS_REGEXP = /([\:\-\_]+(.))/g;
const MOZ_HACK_REGEXP = /^moz([A-Z])/;

/**
 * 判断是否存在于数组中
 * @author weishengjian
 * @date 2018/11/19
 */
function oneOf(val, array) {
	if (!array) {
		return false
	}
	for (let i = 0; i < array.length; i++) {
		const item = array[i];
		if (item === val) return true;
	}
	return false;
}

/**
 * 判断变量类型
 * @author weishengjian
 * @date 2018/11/19
 */
function typeOf(obj) {
	const toString = Object.prototype.toString;
	const map = {
		'[object Boolean]': 'boolean',
		'[object Number]': 'number',
		'[object String]': 'string',
		'[object Function]': 'function',
		'[object Array]': 'array',
		'[object Date]': 'date',
		'[object RegExp]': 'regExp',
		'[object Undefined]': 'undefined',
		'[object Null]': 'null',
		'[object Object]': 'object',
		'[object Promise]': 'promise',
	};
	return map[toString.call(obj)];
}

/**
 * 去掉px,返回数字
 * @author weishengjian
 * @date 2018/11/19
 */
function removePx(value) {
	return String(value).replace(/px/g, '') - 0;
}

/**
 * 深度复制
 * @author weishengjian
 * @date 2018/11/19
 */
function deepCopy(data) {

	if (data == null) return null;

	if (typeof window.AutoOption !== 'undefined' && data instanceof window.AutoOption) return new window.AutoOption(data._initOption);

	const t = typeOf(data);
	let o;
	switch (typeOf(data)) {
		case 'array':
			o = data.map((item) => deepCopy(item));
			break;
		case 'object':
			o = {};
			Object.keys(data).forEach((key) => o[key] = deepCopy(data[key]));
			break;
		case 'date':
			o = new Date();
			o.setTime(data.getTime());
			break;
		case 'regExp':
			const pattern = data.valueOf();
			let flags = '';
			flags += pattern.global ? 'g' : '';
			flags += pattern.ignoreCase ? 'i' : '';
			flags += pattern.multiline ? 'm' : '';
			return new RegExp(pattern.source, flags);
		default:
			o = data;
			break;
	}
	return o;
}

window.deepCopy = deepCopy;

/**
 * 转为驼峰命名
 * @author weishengjian
 * @date 2018/11/19
 */
function camelCase(name) {
	let ret = name.replace(SPECIAL_CHARS_REGEXP, function (_, separator, letter, offset) {
		return offset ? letter.toUpperCase() : letter;
	}).replace(MOZ_HACK_REGEXP, 'Moz$1');
	return ret
}

/**
 * 填充0字符
 * @author weishengjian
 * @date 2018/11/19
 */
function zeroize(value, length = 2) {
	let val = String(value);
	let i = length - val.length;
	while (i > 0) {
		val = '0' + val;
		i--;
	}
	return val;
}

/**
 * 获取唯一标识符
 * @author weishengjian
 * @date 2018/11/19
 */
function uuid() {
	const s = [];
	const hexDigits = '0123456789abcdef';
	for (let i = 0; i < 36; i++) {
		s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
	}
	s[14] = '4'; // bits 12-15 of the time_hi_and_version field to 0010
	s[19] = hexDigits.substr(s[19] & 0x3 | 0x8, 1); // bits 6-7 of the clock_seq_hi_and_reserved to 01
	s[8] = s[13] = s[18] = s[23] = '-';

	const uuid = s.join('');
	return uuid;
}

/**
 * 获取数组 随机下标
 * @author weishengjian
 * @date 2018/11/19
 */
function randomIndex(array) {
	return Boolean(array) && array.length > 0 ? Math.floor(Math.random() * array.length) : 0;
}

/**
 * 从数组中删除
 * @author weishengjian
 * @date 2018/11/19
 */
function removeFromArray(array, target) {
	for (let i = 0; i < array.length; i++) {
		const item = array[i];
		if (item === target) {
			array.splice(i, 1);
			return;
		}
	}
}

function formateDateStirng(string, format = 'yyyy-MM-dd HH:mm:ss') {
	return dateFormat(new Date(string.replace(/-/g, '/')), format);
}

/**
 * 驼峰命名转横杠命名
 * @author weishengjian
 * @date 2018/11/14
 */
function getKebabCase(str) {
	let ret = str.replace(/[A-Z]/g, function (i) {
		return '-' + i.toLowerCase();
	});
	if (ret.charAt(0) === '-') {
		ret = ret.substring(1)
	}
	return ret
}

/**
 * 合并路径
 * @author  韦胜健
 * @date    2019/12/9 14:17
 */
function pathJoin(start, end, separator) {
	separator = separator || '/'
	if (start.endsWith(separator)) {
		start = start.slice(0, start.length - 1)
	}
	if (end.startsWith(separator)) {
		end = end.slice(1)
	}
	if (start == null || start === '') return [separator, end].join('')
	if (end == null || end === '') return [start, separator].join('')
	return [start, separator, end].join('')
}

/**
 * 推迟时间
 * @author weishengjian
 * @date 2018/11/15
 */
function delay(duration = 100, callback) {
	return new Promise((resolve) => {
		setTimeout(() => {
			if (!!callback) {
				callback()
			}
			resolve()
		}, duration)
	})
}

/**
 * 插入排序
 * @author weishengjian
 * @date 2018/11/19
 */
function insertSort(arr, func) {
	const len = arr.length;
	for (let i = 1; i < len; i++) {
		const temp = arr[i];
		/* 默认已排序的元素*/
		let j = i - 1;
		/* 在已排序好的队列中从后向前扫描*/
		while (j >= 0 && func(arr[j], temp)) {
			/* 已排序的元素大于新元素，将该元素移到一下个位置*/
			arr[j + 1] = arr[j];
			j--;
		}
		arr[j + 1] = temp;
	}
	return arr;
}

/**
 * 引入js文件
 * @author  韦胜健
 * @date    2018/11/30 09:46
 */
function addScript(scriptPath) {
	const script = document.createElement('script');
	script.setAttribute('type', 'text/javascript');
	script.setAttribute('src', scriptPath);
	const heads = document.getElementsByTagName('head');
	if (heads.length)
		heads[0].appendChild(script);
	else
		document.documentElement.appendChild(script);
}

/**
 * 格式化日期对象
 * @author  韦胜健
 * @date    2018/12/13 10:29
 * @param date 将要格式化的日期
 * @param format 格式化字符串
 */
function dateFormat(date, format) {
	if (!date) return null;
	format = format || 'YYYY-MM-DD';
	return fecha.format(date, format);
}

/**
 * 解析字符串为日期对象
 * @author  韦胜健
 * @date    2018/12/13 10:30
 * @param string 将要解析的字符串
 * @param format 格式化字符串
 */
function dateParse(string, format) {
	if (string == null) return null;
	if (typeof string === "number") {
		string = String(string)
	}
	format = format || 'YYYY-MM-DD';
	return fecha.parse(string, format);
}

/**
 * 解析日期对象中的数据信息
 * @author  韦胜健
 * @date    2019/3/11 20:24
 */
function decodeDate(date) {
	const ret = {
		year: null,
		month: null,
		day: null,
		hour: null,
		minute: null,
		second: null,
		date: null,
		time: null,
		timeString: null,
	}
	if (!date) return ret
	ret.year = date.getFullYear()
	ret.month = date.getMonth()
	ret.day = date.getDate()
	ret.hour = date.getHours()
	ret.minute = date.getMinutes()
	ret.second = date.getSeconds()
	ret.date = date
	ret.time = date.getTime()
	ret.timeString = `${zeroize(ret.hour)}:${zeroize(ret.minute)}:${zeroize(ret.second)}`
	return ret
}

/**
 * 自动补全单位，如果是字符串，则返回字符串，如果是数字，则添加px单位
 * @author  韦胜健
 * @date    2018/12/28 16:24
 */
function unit(val) {
	if (val == null) return null
	if (/^[\d]+$/.test(String(val))) {
		return `${val}px`
	}
	return val
}

function shuffle(array) {
	array = deepCopy(array);
	let currentIndex = array.length;
	let randomIndex, temporaryValue;
	// While there remain elements to shuffle...
	while (currentIndex !== 0) {
		// Pick a remaining element...
		randomIndex = Math.floor(Math.random() * currentIndex);
		currentIndex -= 1;
		// And swap it with the current element.
		temporaryValue = array[currentIndex];
		array[currentIndex] = array[randomIndex];
		array[randomIndex] = temporaryValue;
	}
	return array;
}

/**
 * 从数组中查找一个
 * @author  韦胜健
 * @date    2019/1/10 10:56
 */
function findOne(array, fn, indexing) {
	for (let i = 0; i < array.length; i++) {
		const item = array[i];
		if (Boolean(fn(item))) return !indexing ? item : {item, index: i};
	}
	return null;
}

/**
 * 从数组中查找多个
 * @author  韦胜健
 * @date    2019/1/10 10:57
 */
function findSome(array, fn) {
	const ret = [];
	for (let i = 0; i < array.length; i++) {
		const item = array[i];
		if (Boolean(fn(item))) ret.push(item);
	}
	return ret;
}

/**
 * desc 手机号码格式化
 * @author huyang
 * @date 2018/12/11 13:43
 * @params
 */
function telFormat(d) {
	if (d == null) return '';
	d = String(d);
	d = d.trim();
	if (d.length >= 11) {
		const d1 = d.substr(d.length - 4, 4);
		const d2 = d.substr(d.length - 8, 4);
		const d3 = d.substr(d.length - 11, 3);
		return d3 + ' ' + d2 + ' ' + d1;
	} else {
		return d;
	}
}

/**
 * desc 格式化为整数
 * @author huyang
 * @date 2018/12/11 13:45
 * @params
 */
function intFormat(val) {
	return val && String(parseInt(val)).replace(/\d{1,3}(?=(\d{3})+(\.\d*)?$)/g, '$&,');
}

/**
 * desc 金额格式化，并保留两位小数
 * @author huyang
 * @date 2018/12/11 13:45
 * @params
 */
function moneyFormat(val) {
	if (val || val == 0) {
		val = val.toString().replace(/\$|\, /g, '');
		if (isNaN(val)) {
			val = '0';
		}
		const sign = val == (val = Math.abs(val));
		val = Math.floor(val * 100 + 0.50000000001);
		let cents = val % 100;
		val = Math.floor(val / 100).toString();
		if (cents < 10) {
			cents = '0' + cents;
		}
		for (let i = 0; i < Math.floor((val.length - (1 + i)) / 3); i++) {
			val = val.substring(0, val.length - (4 * i + 3)) + ',' + val.substring(val.length - (4 * i + 3));
		}
		return (sign ? '' : '-') + val + '.' + cents;
	}
}

/**
 * desc 转化为人民币显示，并保留两位小数
 * @author huyang
 * @date 2018/12/11 13:47
 * @params
 */
function cnyFormat(d) {
	// 转换为人民币显示
	if (!d) {
		return '￥0';
	}
	if (isNaN(d)) {
		// return '无效的金额';
		return d
	}
	const dd = parseFloat(d);
	return '￥' + String(dd.toFixed(2)).replace(/\d{1,3}(?=(\d{3})+(\.\d*)?$)/g, '$&,');
}

/**
 * desc 转化为百分比显示，并保留两位小数
 * @author huyang
 * @date 2018/12/11 13:47
 * @params
 */
function percentNumFormat(d) {
	// 转换为两位小数
	if (d == null) {
		return '';
	}
	if (isNaN(d)) {
		return '';
	}
	return (parseFloat(d) * 100).toFixed(2) + '%';
}

/**
 * desc 去除文件后缀名
 * @author huyang
 * @date 2018/12/11 13:47
 * @params
 */
function getFileName(name) {
	let json = name.split(".")
	return json[0];
}

/**
 * 使用encodeURIComponent转义字符串
 * @author  韦胜健
 * @date    2019/10/16 11:39
 */
function encodeString(str) {
	return encodeURIComponent(str)
		.replace(/%40/g, '@')
		.replace(/%3A/ig, ':')
		.replace(/%24/g, '$')
		.replace(/%2C/ig, ',')
		.replace(/%20/g, '+')
		.replace(/%5B/ig, '[')
		.replace(/%5D/ig, ']')
		.replace(/%2f/ig, '/')
}

/**
 * 生成url地址
 * @author  韦胜健
 * @date    2019/4/2 10:06
 */
function encodeUrl(url, params) {
	if (!params) return url
	const serializedParams = encodeParam(params)
	if (serializedParams) {
		const markIndex = url.indexOf('#')
		if (markIndex !== -1) url = url.slice(0, markIndex)
		url += (url.indexOf('?') === -1 ? '?' : '&') + serializedParams
	}
	return url
}

/**
 * 将对象格式化成表单参数字符串
 * @author  韦胜健
 * @date    2019/10/21 14:56
 */
function encodeParam(params) {
	if (!params) return ''
	const parts = []
	Object.keys(params).forEach(key => {
		const val = params[key]
		if (val === null || typeof val === 'undefined') {
			return
		}
		let values = []
		if (Array.isArray(val)) {
			values = val
			key += '[]'
		} else {
			values = [val]
		}

		values.forEach(val => {
			if (typeOf(val) === 'date') {
				val = val.toISOString()
			} else if (typeOf(val) === 'object') {
				val = JSON.stringify(val)
			}
			parts.push(`${encodeString(key)}=${encodeString(val)}`)
		})
	})

	return parts.join('&')
}

/**
 * 解析url地址
 * @author  韦胜健
 * @date    2019/4/2 10:48
 */
function decodeUrl(url) {
	let param = {}
	let pureUrl
	let urlParts = url.split('?')
	if (urlParts.length === 0) {
		console.error("地址解析失败：" + url)
		return null
	}
	pureUrl = urlParts[0]
	if (urlParts.length === 2) {
		const paramStrings = urlParts[1].trim().split("&");
		paramStrings.forEach((str) => {
			const [key, value] = str.split('=')
			param[decodeURIComponent(key)] = decodeURIComponent(value)
		})
	}
	return {url: pureUrl, param};
}

/**
 * 判断是否为数字
 * @author  韦胜健
 * @date    2019/5/6 09:55
 */
function isNumber(obj) {
	return typeof obj === 'number' && !isNaN(obj)
}

function chunk(array, size) {
	// #1
	size = Math.max(size, 0)
	const length = array == null ? 0 : array.length
	if (!length || size < 1) {
		return []
	}

	// #2
	let index = 0
	let resIndex = 0
	const result = new Array(Math.ceil(length / size))

	// #3
	while (index < length) {
		result[resIndex++] = array.slice(index, index += size)
	}
	return result
}

/**
 * 复制内容到剪切板
 * @author  韦胜健
 * @date    2019/4/4 15:51
 */


function copyToClipboard(text, success, error) {
	var textArea = document.createElement("textarea");
	textArea.style.position = 'fixed';
	textArea.style.top = '0';
	textArea.style.left = '0';
	textArea.style.width = '2em';
	textArea.style.height = '2em';
	textArea.style.padding = '0';
	textArea.style.border = 'none';
	textArea.style.outline = 'none';
	textArea.style.boxShadow = 'none';
	textArea.style.opacity = 0;
	textArea.style.zIndex = -1000;
	textArea.value = text;
	document.body.appendChild(textArea);
	textArea.select();

	try {
		var successful = document.execCommand('copy');
		if (successful) !!success && success(); else !!error && error("复制到剪切板失败！");
	} catch (err) {
		!!error && error(err);
	}

	document.body.removeChild(textArea);
}

/**
 * 节流
 * @author  韦胜健
 * @date    2019/11/21 9:30
 * @param   func:Function                       执行函数
 * @param   wait:number = 500                   节流时长（该时间段内，执行一次func）
 * @param   options                             其他参数
 * @param   options.trailing:boolean = true     是否最后一次应该被处罚
 * @param   options.leading:boolean = true      第一次是否立即执行
 */
function throttle(func, wait = 500, options) {
	if (!func) {
		throw new Error('节流函数不能为空！')
	}
	options = options || {}
	options.trailing = options.trailing != null ? options.trailing : true
	options.leading = options.leading != null ? options.leading : true

	let args;                       //func执行参数
	let context;                    //func执行上下文
	let previous = 0;               //func上一次执行时间戳
	let timeout;                    //定时器对象

	let later = () => {
		previous = options.leading === false ? 0 : Date.now()
		func.apply(context, args)
		args = context = timeout = null
	}

	return function () {
		args = arguments
		context = this
		let now = Date.now()

		/*上一次执行时间为0，并且不延迟第一次触发*/
		if (!previous && options.leading === false) {
			previous = now
		}

		/*准备执行func函数的等待时间*/
		let remaining = wait - (now - previous)

		if (remaining <= 0) {
			/*第一次*/
			if (timeout) {
				clearTimeout(timeout)
				timeout = null
			}
			// console.log('throttle click：' + Date.now() % 10000)
			func.apply(context, args)
			previous = now

		} else if (!timeout && options.trailing !== false) {
			timeout = setTimeout(later, remaining)
		}
	}
}

/**
 * desc 数字并保留两位小数
 * @author 秦学刚·
 * @date 2018/12/11 13:47
 * @params
 */
function numberToFixed(val) {
	return parseFloat(val).toFixed(2);
}

/**
 * 防抖
 * @author  韦胜健
 * @date    2019/11/21 9:42
 * @param   func:Function               执行函数
 * @param   wait                        时间间隔
 * @param   immediate                   第一次是否立即执行
 */
function debounce(func, wait = 500, immediate = false) {
	let timeout;

	return function (...args) {
		// @ts-ignore
		const context = this
		clearTimeout(timeout)
		if (immediate) {
			let callNow = !timeout
			// @ts-ignore
			if (callNow) func.apply(this, args)
		}
		timeout = setTimeout(() => {
			func.apply(context, args)
			timeout = null
		}, wait)
	}
}

function defer() {
	const ret = {}
	ret.promise = new Promise((resolve, reject) => {
		ret.resolve = resolve
		ret.reject = reject
	})
	return ret
}

function AsyncWorks(works) {
	this.works = works || []
	this.flowing = false

	this.push = (work) => {
		this.works.push(work)
	}
	this.unshift = (work) => {
		this.works.unshift(work)
	}

	this.clear = () => {
		this.works = []
	}

	this.start = async (callback) => {
		this.flowing = true

		const defer = LinkVue.$utils.defer()
		let work = this.works.shift()
		if (!!work) {
			work().then(() => {
				if (!this.flowing) return
				this.start(callback)
			}, defer.reject)
		} else {
			if (!!callback) callback();
			defer.resolve()
			this.flowing = false
		}
		return defer.promise
	}
	this.stop = () => {
		this.flowing = false
	}
	this.append = (work) => {
		this.push(work)
		if (!this.flowing) this.start()
	}
}

export const deepEqual = (source, target) => {

	switch (typeOf(source)) {
		case 'number':
			return Number(source) === Number(target)
		case 'string':
			return source == target
		case 'boolean':
		case 'function':
			return source === target
		case 'null':
		case 'undefined':
			return source == target
		case 'array':
			if (typeOf(target) !== 'array') return false
			let len = source.length > target.length ? source.length : target.length
			for (let i = 0; i < len; i++) {
				const item = source[i];
				let targetItem = target[i]
				let flag = deepEqual(item, targetItem)
				if (!flag) return false
			}
			return true
		case 'object':
			if (typeOf(target) !== 'object') return false
			let keys = Array.from(new Set([...Object.keys(source), ...Object.keys(target)]))
			for (let i = 0; i < keys.length; i++) {
				const key = keys[i];
				let flag = deepEqual(source[key], target[key])
				if (!flag) return false
			}
			return true
	}

	return false
}

const LinkUtils = {
	processAxiosConfig,
	getKebabCase,       // 驼峰命名转横杠命名
	kebabCase: getKebabCase,
	camelCase,          // 转为驼峰命名
	pathJoin,           //合并路径

	chunk,
	insertSort,         // 插入排序
	removeFromArray,    // 从数组中删除

	dateFormat,         // 日期格式化
	dateParse,          // 字符串格式化为日期
	decodeDate,         // 解析日期对象信息

	shuffle,            // 打乱数组顺序
	findOne,            // 从数组中查找一个
	findSome,           // 从数组中查找多个

	uuid,               // 获取唯一标识符
	zeroize,            // 填充0字符
	removePx,           // 去掉px,返回数字
	unit,               // 填补单位，如果是字符串，则返回原数据，如果是数字则返回数字+px
	parseJson,

	addScript,          // 引入js文件
	delay,              // 推迟时间


	deepCopy,           // 深度复制
	typeOf,             // 判断变量类型
	keyboard,
	encodeUrl,
	decodeUrl,
	encodeParam,

	telFormat,          // 电话号码格式化
	intFormat,          // 格式化为整数
	moneyFormat,        // 金额格式化，保留两位消暑
	cnyFormat,          // 人民币格式化
	percentNumFormat,   // 百分比格式化

	getFileName,        // 去除文件后缀名
	isNumber,
	oneOf,              // 判断是否存在于数组中

	copyToClipboard,    //复制到剪切板
	throttle,           //节流
	debounce,           //防抖
	defer,              //Promise.defer
	AsyncWorks,
	numberToFixed,		// 数字并保留两位小数
	deepEqual,
};

if (typeof window.LinkUtils === 'undefined') {
	window.LinkUtils = LinkUtils
} else {
	window.LinkUtils = {
		...window.LinkUtils,
		...LinkUtils,
	}
}

export default LinkUtils
